/**
 * Training Types for MEV-Med Frontend
 * Type definitions for training management
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para gestão de treinamentos de funcionários
 */

/**
 * Enum for Training Type (NRs and categories)
 */
export enum TrainingNRType {
  NR_06 = 'NR_06',   // EPIs
  NR_10 = 'NR_10',   // Eletricidade
  NR_12 = 'NR_12',   // Máquinas
  NR_18 = 'NR_18',   // Construção Civil
  NR_20 = 'NR_20',   // Inflamáveis
  NR_33 = 'NR_33',   // Espaço Confinado
  NR_35 = 'NR_35',   // Trabalho em Altura
  CIPA = 'CIPA',     // Comissão Interna
  FIRST_AID = 'FIRST_AID', // Primeiros Socorros
  FIRE_BRIGADE = 'FIRE_BRIGADE', // Brigada de Incêndio
  DEFENSIVE_DRIVING = 'DEFENSIVE_DRIVING', // Direção Defensiva
  OTHER = 'OTHER'    // Outros
}

/**
 * Labels for Training Type display
 */
export const TrainingNRTypeLabels: Record<TrainingNRType, string> = {
  [TrainingNRType.NR_06]: 'NR-06 - EPIs',
  [TrainingNRType.NR_10]: 'NR-10 - Eletricidade',
  [TrainingNRType.NR_12]: 'NR-12 - Máquinas',
  [TrainingNRType.NR_18]: 'NR-18 - Construção Civil',
  [TrainingNRType.NR_20]: 'NR-20 - Inflamáveis',
  [TrainingNRType.NR_33]: 'NR-33 - Espaço Confinado',
  [TrainingNRType.NR_35]: 'NR-35 - Trabalho em Altura',
  [TrainingNRType.CIPA]: 'CIPA',
  [TrainingNRType.FIRST_AID]: 'Primeiros Socorros',
  [TrainingNRType.FIRE_BRIGADE]: 'Brigada de Incêndio',
  [TrainingNRType.DEFENSIVE_DRIVING]: 'Direção Defensiva',
  [TrainingNRType.OTHER]: 'Outros'
};

/**
 * Enum for Training Category
 */
export enum TrainingCategory {
  INITIAL = 'INITIAL',     // Treinamento inicial (primeira vez)
  PERIODIC = 'PERIODIC',   // Reciclagem periódica obrigatória
  EVENTUAL = 'EVENTUAL'    // Treinamento eventual (mudança de função, retorno, etc)
}

/**
 * Labels for Training Category display
 */
export const TrainingCategoryLabels: Record<TrainingCategory, string> = {
  [TrainingCategory.INITIAL]: 'Inicial',
  [TrainingCategory.PERIODIC]: 'Reciclagem',
  [TrainingCategory.EVENTUAL]: 'Eventual'
};

/**
 * Enum for Training Status
 */
export enum TrainingStatus {
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED'
}

/**
 * Labels for Training Status display
 */
export const TrainingStatusLabels: Record<TrainingStatus, string> = {
  [TrainingStatus.ACTIVE]: 'Ativo',
  [TrainingStatus.EXPIRED]: 'Expirado',
  [TrainingStatus.CANCELLED]: 'Cancelado'
};

/**
 * Colors for Training Status display
 */
export const TrainingStatusColors: Record<TrainingStatus, 'success' | 'error' | 'default'> = {
  [TrainingStatus.ACTIVE]: 'success',
  [TrainingStatus.EXPIRED]: 'error',
  [TrainingStatus.CANCELLED]: 'default'
};

/**
 * Enum for validity status (calculated based on days until expiry)
 */
export enum TrainingValidityStatus {
  VALID = 'VALID',       // Green - More than 30 days until expiry
  WARNING = 'WARNING',   // Yellow - 30 days or less until expiry
  EXPIRED = 'EXPIRED'    // Red - Past expiry date
}

/**
 * Labels for validity status display
 */
export const TrainingValidityStatusLabels: Record<TrainingValidityStatus, string> = {
  [TrainingValidityStatus.VALID]: 'Vigente',
  [TrainingValidityStatus.WARNING]: 'Vencendo',
  [TrainingValidityStatus.EXPIRED]: 'Vencido'
};

/**
 * Colors for validity status display
 */
export const TrainingValidityStatusColors: Record<TrainingValidityStatus, 'success' | 'warning' | 'error'> = {
  [TrainingValidityStatus.VALID]: 'success',
  [TrainingValidityStatus.WARNING]: 'warning',
  [TrainingValidityStatus.EXPIRED]: 'error'
};

/**
 * Training entity interface
 */
export interface Training {
  id: number;
  employeeId: number;
  employeeName?: string;
  employeeCpf?: string;
  companyName?: string;
  departmentId?: number;
  departmentName?: string;
  occupationId?: number;
  occupationName?: string;
  instructorId?: number;
  instructorName?: string;
  trainingTypeId?: number;
  name: string;
  type?: TrainingNRType;
  category: TrainingCategory;
  description?: string;
  activityDescription?: string;
  workload?: number;
  completedAt: string;
  expiresAt: string;
  certificatePath?: string;
  esocialCode?: string;
  sendToEsocial: boolean;
  esocialEventId?: string;
  esocialStatus?: string;
  status: TrainingStatus;
  validityStatus: TrainingValidityStatus;
  daysUntilExpiry: number;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

/**
 * Filters for listing trainings
 */
export interface TrainingFilters {
  search?: string;
  employeeId?: number;
  departmentId?: number;
  occupationId?: number;
  instructorId?: number;
  type?: TrainingNRType;
  category?: TrainingCategory;
  validityStatus?: TrainingValidityStatus;
  status?: TrainingStatus;
  sendToEsocial?: boolean;
  esocialStatus?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Form data for creating/updating trainings
 */
export interface TrainingFormData {
  employeeId: number;
  departmentId?: number;
  occupationId?: number;
  instructorId?: number;
  trainingTypeId?: number;
  name: string;
  type?: TrainingNRType;
  category: TrainingCategory;
  description?: string;
  activityDescription?: string;
  workload?: number;
  completedAt: string;
  expiresAt: string;
  certificatePath?: string;
  esocialCode?: string;
  sendToEsocial: boolean;
  esocialEventId?: string;
  esocialStatus?: string;
  status?: TrainingStatus;
}

/**
 * API payload for creating/updating trainings
 */
export interface TrainingApiPayload {
  employeeId: number;
  departmentId?: number;
  occupationId?: number;
  instructorId?: number;
  trainingTypeId?: number;
  name: string;
  type?: TrainingNRType;
  category: TrainingCategory;
  description?: string;
  activityDescription?: string;
  workload?: number;
  completedAt: string;
  expiresAt: string;
  certificatePath?: string;
  esocialCode?: string;
  sendToEsocial?: boolean;
  esocialEventId?: string;
  esocialStatus?: string;
  status?: TrainingStatus;
}

/**
 * Response for list endpoint with pagination
 */
export interface TrainingListResponse {
  trainings: Training[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Statistics response
 */
export interface TrainingStatistics {
  total: number;
  active: number;
  expired: number;
  cancelled: number;
  expiringSoon: number;
}

/**
 * Props for UI components
 */
export interface TrainingTableProps {
  trainings: Training[];
  onEdit?: (training: Training) => void;
  onDelete?: (id: number) => void;
  onView?: (training: Training) => void;
  loading?: boolean;
}

export interface TrainingFormProps {
  initialData?: Training;
  onSubmit: (data: TrainingFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  employees?: Array<{ id: number; fullName: string; cpf?: string; companyName?: string }>;
  departments?: Array<{ id: number; name: string }>;
  occupations?: Array<{ id: number; name: string }>;
  instructors?: Array<{ id: number; name: string }>;
}

export interface TrainingFiltersProps {
  filters: TrainingFilters;
  onChange: (filters: TrainingFilters) => void;
  employees?: Array<{ id: number; fullName: string }>;
}

export interface TrainingValidityStatusChipProps {
  status: TrainingValidityStatus;
  daysUntilExpiry?: number;
  size?: 'small' | 'medium';
}

export interface TrainingStatusChipProps {
  status: TrainingStatus;
  size?: 'small' | 'medium';
}
